import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  MASTER_DATA_DOMAINS,
  type MasterDataDomain,
  type MasterDataValue,
  type MasterDataValueStatus,
} from '@/types/platform';
import { formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-15 — the reference data every product reads.
 *
 * SIXTEEN DOMAINS, NOT EIGHT SETS. This screen used to offer COUNTRY, CURRENCY,
 * LANGUAGE, TIMEZONE, GRADING_SCALE, DOCUMENT_TYPE, ROLE_CATALOGUE and
 * PRODUCT_CATALOGUE. Core's MasterDataDomain has sixteen members and three of
 * those eight are not among them, so half the tabs pointed at 400s and the domains
 * that do exist — PERMISSION_TEMPLATE, MERCHANT_CATEGORY, WORKFLOW_TEMPLATE and
 * the rest — were unreachable.
 *
 * THERE IS NO UPDATE, AND THAT IS THE FEATURE. A change publishes a NEW VERSION
 * and end-dates the old one. The editor here used to PATCH a row in place with a
 * `labelFr` and an `active` boolean, neither of which core has; because the DTO
 * forbids unrecognised fields the whole request was a 400, so nothing was ever
 * saved. Editing in place would also silently rewrite what every historical record
 * meant, which is why core does not offer it.
 *
 * THERE IS NO DELETE EITHER. A code is cited by rows that already exist. Deleting
 * one does not remove the references; it leaves them pointing at nothing, and the
 * failure surfaces months later as a blank column in an export nobody can explain.
 * Values are retired instead: they stop being offered on new records and keep
 * meaning what they meant on old ones.
 *
 * RETIREMENT IS PREVIEWED BEFORE IT IS ATTEMPTED. The refusal must not be the
 * first time somebody learns that four hundred records point at this code — and
 * the preview says separately which services could not be checked at all, because
 * "nothing in core cites it" is not "nothing uses it".
 */

const STATUS_TONES: Record<MasterDataValueStatus, string> = {
  DRAFT: 'warnTag',
  ACTIVE: 'okTag',
  DEPRECATED: 'warningTag',
  SUPERSEDED: 'mutedTag',
  RETIRED: 'mutedTag',
};

/** What `POST :domain/:code/status` will accept as a target. */
const SETTABLE_STATUSES: readonly MasterDataValueStatus[] = ['ACTIVE', 'DEPRECATED'];

const MIN_REASON = 10;

type ValueDialog =
  | { kind: 'CREATE' }
  | { kind: 'VERSION'; value: MasterDataValue }
  | { kind: 'STATUS'; value: MasterDataValue }
  | { kind: 'RETIRE'; value: MasterDataValue };

function isDomain(value: string | null): value is MasterDataDomain {
  return (MASTER_DATA_DOMAINS as readonly string[]).includes(value ?? '');
}

export function MasterDataPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [params, setParams] = useSearchParams();
  const domainParam = params.get('domain');
  const domain: MasterDataDomain = isDomain(domainParam) ? domainParam : MASTER_DATA_DOMAINS[0];
  const includeRetired = params.get('includeRetired') === 'true';
  const [dialog, setDialog] = useState<ValueDialog | null>(null);
  const [inspecting, setInspecting] = useState<MasterDataValue | null>(null);

  const fetcher = useCallback(
    () => platformApi.masterData.list(domain, includeRetired),
    [domain, includeRetired],
  );
  const { state, reload } = useAsyncData<MasterDataValue[]>(
    `master-data:${domain}:${includeRetired}`,
    fetcher,
  );

  const setDomain = (next: MasterDataDomain) => {
    setParams(includeRetired ? { domain: next, includeRetired: 'true' } : { domain: next });
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.masterData.title')}</h1>
          <p className={styles.pageLead}>{t('platform.masterData.lead')}</p>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setDialog({ kind: 'CREATE' })}>
          <Icon name="plus" size={16} />
          {t('platform.masterData.add')}
        </button>
      </header>

      <div className={styles.tabBar} role="tablist" aria-label={t('platform.masterData.domainsLabel')}>
        {MASTER_DATA_DOMAINS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            id={`master-tab-${candidate}`}
            aria-selected={domain === candidate}
            aria-controls="master-results"
            className={`${styles.tab} ${domain === candidate ? styles.tabActive : ''}`}
            onClick={() => setDomain(candidate)}
          >
            {t(`platform.masterData.domain.${candidate}`, { defaultValue: candidate })}
          </button>
        ))}
      </div>

      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.masterData.retireNotice')}
      </p>

      <div id="master-results" role="tabpanel" aria-labelledby={`master-tab-${domain}`}>
        <Panel
          title={t(`platform.masterData.domain.${domain}`, { defaultValue: domain })}
          icon="checklist"
          actions={
            <>
              <Checkbox
                label={t('platform.masterData.includeRetired')}
                checked={includeRetired}
                onChange={(event) =>
                  setParams(event.target.checked ? { domain, includeRetired: 'true' } : { domain })
                }
              />
              <button type="button" className={styles.secondaryBtn} onClick={reload}>
                <Icon name="refresh" size={15} />
                {t('platform.dashboard.refresh')}
              </button>
            </>
          }
        >
          <AsyncPanel
            state={state}
            onRetry={reload}
            isEmpty={(data) => data.length === 0}
            emptyTitle={t('platform.masterData.emptyTitle')}
            emptyBody={t('platform.masterData.emptyBody')}
          >
            {(data) => (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <caption className={styles.tableCaption}>
                    {t('platform.masterData.tableCaption', {
                      domain: t(`platform.masterData.domain.${domain}`, { defaultValue: domain }),
                      n: data.length,
                    })}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('platform.masterData.colCode')}</th>
                      <th scope="col">{t('platform.masterData.colLabel')}</th>
                      <th scope="col">{t('platform.masterData.colVersion')}</th>
                      <th scope="col">{t('platform.masterData.colStatus')}</th>
                      <th scope="col">{t('platform.masterData.colEffective')}</th>
                      <th scope="col">
                        <span className={styles.srOnlyText}>{t('platform.masterData.colActions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((entry) => (
                      <tr key={entry.id}>
                        <th scope="row" className={styles.tableRowHeader}>
                          <code className={styles.code}>{entry.code}</code>
                        </th>
                        <td>
                          {entry.label}
                          {entry.description && <span className={styles.tableSub}>{entry.description}</span>}
                        </td>
                        <td className={styles.mutedCell}>
                          {t('platform.masterData.versionNumber', { n: entry.version })}
                        </td>
                        <td>
                          <span className={styles[STATUS_TONES[entry.status]] ?? styles.mutedTag}>
                            {t(`platform.masterData.status.${entry.status}`, { defaultValue: entry.status })}
                          </span>
                          {entry.retiredReason && <span className={styles.tableSub}>{entry.retiredReason}</span>}
                        </td>
                        <td className={styles.mutedCell}>
                          {formatDateTime(entry.effectiveFrom, locale)}
                          {entry.effectiveTo ? ` → ${formatDateTime(entry.effectiveTo, locale)}` : ''}
                        </td>
                        <td>
                          <div className={styles.rowAction}>
                            <button
                              type="button"
                              className={styles.linkBtn}
                              onClick={() => setInspecting(entry)}
                            >
                              {t('platform.masterData.inspect')}
                            </button>
                            {entry.status !== 'RETIRED' && (
                              <>
                                <button
                                  type="button"
                                  className={styles.linkBtn}
                                  onClick={() => setDialog({ kind: 'VERSION', value: entry })}
                                >
                                  {t('platform.masterData.newVersion')}
                                </button>
                                <button
                                  type="button"
                                  className={styles.linkBtn}
                                  onClick={() => setDialog({ kind: 'STATUS', value: entry })}
                                >
                                  {t('platform.masterData.changeStatus')}
                                </button>
                                <button
                                  type="button"
                                  className={styles.linkBtnDanger}
                                  onClick={() => setDialog({ kind: 'RETIRE', value: entry })}
                                >
                                  {t('platform.masterData.retire')}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AsyncPanel>
        </Panel>
      </div>

      <ValueDialogView
        dialog={dialog}
        domain={domain}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          reload();
        }}
      />

      <ValueHistoryDialog
        domain={domain}
        value={inspecting}
        onClose={() => setInspecting(null)}
        locale={locale}
      />
    </div>
  );
}

function ValueHistoryDialog({
  domain,
  value,
  onClose,
  locale,
}: {
  domain: MasterDataDomain;
  value: MasterDataValue | null;
  onClose: () => void;
  locale: string;
}) {
  const { t } = useTranslation();
  const code = value?.code ?? '';
  const enabled = code.length > 0;

  const historyFetcher = useCallback(() => platformApi.masterData.history(domain, code), [domain, code]);
  const { state: history, reload: reloadHistory } = useAsyncData<MasterDataValue[]>(
    `master-history:${domain}:${code}`,
    historyFetcher,
    enabled,
  );

  const usageFetcher = useCallback(() => platformApi.masterData.usage(domain, code), [domain, code]);
  const { state: usage, reload: reloadUsage } = useAsyncData(
    `master-usage:${domain}:${code}`,
    usageFetcher,
    enabled,
  );

  if (!value) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={value.code}
      description={t('platform.masterData.inspectLead', { label: value.label })}
    >
      <div className={styles.auditDetail}>
        <section>
          <h3 className={styles.subheading}>{t('platform.masterData.usageTitle')}</h3>
          <AsyncPanel state={usage} onRetry={reloadUsage}>
            {(report) => (
              <>
                {report.observed.length === 0 ? (
                  <p className={styles.sectionEmpty}>{t('platform.masterData.noObservedUsage')}</p>
                ) : (
                  <ul className={styles.codeList}>
                    {report.observed.map((entry) => (
                      <li key={entry.source}>
                        {entry.source}: {entry.count}
                      </li>
                    ))}
                  </ul>
                )}
                {/*
                  The blind spots sit next to the counts, not below a fold. An empty
                  observed list means nothing in core cites this value; it does not
                  mean nothing uses it, and the services listed here cannot be
                  queried from this process at all.
                */}
                <div className={styles.warningNotice} role="note">
                  <Icon name="warning" size={16} aria-hidden="true" />
                  <div>
                    <p className={styles.warningTitle}>{t('platform.masterData.blindSpotsTitle')}</p>
                    <p className={styles.warningBody}>
                      {report.blindSpots.length > 0
                        ? t('platform.masterData.blindSpotsBody', { services: report.blindSpots.join(', ') })
                        : t('platform.masterData.noBlindSpots')}
                    </p>
                  </div>
                </div>
              </>
            )}
          </AsyncPanel>
        </section>

        <section>
          <h3 className={styles.subheading}>{t('platform.masterData.historyTitle')}</h3>
          <AsyncPanel
            state={history}
            onRetry={reloadHistory}
            isEmpty={(data) => data.length === 0}
            emptyTitle={t('platform.masterData.noHistory')}
          >
            {(data) => (
              <ul className={styles.timeline}>
                {data.map((entry) => (
                  <li key={entry.id} className={styles.timelineItem}>
                    <span className={styles.timelineDot} aria-hidden="true" />
                    <div>
                      <p className={styles.rowTitle}>
                        {t('platform.masterData.versionNumber', { n: entry.version })} · {entry.label}
                      </p>
                      <p className={styles.rowMetaLine}>
                        {t(`platform.masterData.status.${entry.status}`, { defaultValue: entry.status })}
                        {' · '}
                        {formatDateTime(entry.effectiveFrom, locale)}
                        {entry.effectiveTo ? ` → ${formatDateTime(entry.effectiveTo, locale)}` : ''}
                        {entry.createdBy ? ` · ${entry.createdBy}` : ''}
                      </p>
                      {entry.changeReason && <p className={styles.rowDetail}>{entry.changeReason}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </AsyncPanel>
        </section>
      </div>

      <div className="modal-actions">
        <button type="button" className="modal-button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Modal>
  );
}

function ValueDialogView({
  dialog,
  domain,
  onClose,
  onSaved,
}: {
  dialog: ValueDialog | null;
  domain: MasterDataDomain;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [payloadText, setPayloadText] = useState('{}');
  const [publishImmediately, setPublishImmediately] = useState(true);
  const [changeReason, setChangeReason] = useState('');
  const [status, setStatus] = useState<MasterDataValueStatus>('ACTIVE');
  const [acknowledgeUnverifiable, setAcknowledgeUnverifiable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const target = dialog && dialog.kind !== 'CREATE' ? dialog.value : null;
  const previewCode = target?.code ?? '';
  const previewEnabled = dialog?.kind === 'RETIRE' && previewCode.length > 0;

  const previewFetcher = useCallback(
    () => platformApi.masterData.retirementPreview(domain, previewCode),
    [domain, previewCode],
  );
  const { state: preview, reload: reloadPreview } = useAsyncData(
    `master-retire-preview:${domain}:${previewCode}`,
    previewFetcher,
    previewEnabled,
  );

  useEffect(() => {
    if (!dialog) return;
    setError(null);
    setChangeReason('');
    setAcknowledgeUnverifiable(false);
    if (dialog.kind === 'CREATE') {
      setCode('');
      setLabel('');
      setDescription('');
      setPayloadText('{}');
      setPublishImmediately(true);
      return;
    }
    setCode(dialog.value.code);
    setLabel(dialog.value.label);
    setDescription(dialog.value.description ?? '');
    setPayloadText(JSON.stringify(dialog.value.payload ?? {}, null, 2));
    setStatus(dialog.value.status === 'DEPRECATED' ? 'ACTIVE' : 'DEPRECATED');
  }, [dialog]);

  const payload = useMemo(() => {
    try {
      const value: unknown = JSON.parse(payloadText);
      if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
      return value as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [payloadText]);

  if (!dialog) return null;

  const validCode = /^[A-Z0-9][A-Z0-9_]*$/.test(code.trim());
  const valid =
    dialog.kind === 'CREATE'
      ? validCode && label.trim().length >= 2 && payload !== null
      : dialog.kind === 'VERSION'
        ? label.trim().length >= 2 && payload !== null && changeReason.trim().length >= MIN_REASON
        : changeReason.trim().length >= MIN_REASON;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (dialog.kind === 'CREATE') {
        await platformApi.masterData.create(domain, {
          code: code.trim().toUpperCase(),
          label: label.trim(),
          description: description.trim() || undefined,
          payload: payload ?? undefined,
          publishImmediately,
          changeReason: changeReason.trim() || undefined,
        });
      } else if (dialog.kind === 'VERSION') {
        await platformApi.masterData.publishVersion(domain, dialog.value.code, {
          label: label.trim(),
          description: description.trim() || undefined,
          payload: payload ?? undefined,
          changeReason: changeReason.trim(),
        });
      } else if (dialog.kind === 'STATUS') {
        await platformApi.masterData.changeStatus(domain, dialog.value.code, {
          status,
          reason: changeReason.trim(),
        });
      } else {
        await platformApi.masterData.retire(domain, dialog.value.code, {
          reason: changeReason.trim(),
          acknowledgeUnverifiableUsage: acknowledgeUnverifiable || undefined,
        });
      }
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`platform.masterData.dialog.${dialog.kind}.title`, { code: previewCode })}
      description={t(`platform.masterData.dialog.${dialog.kind}.lead`)}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        {dialog.kind === 'CREATE' && (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.masterData.colCode')}</span>
            <input
              className={styles.input}
              value={code}
              required
              autoComplete="off"
              aria-invalid={code.length > 0 && !validCode}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
            />
            {/*
              The pattern is enforced in core's domain layer, not only its DTO,
              because codes also arrive from seed migrations and imports. A code
              with a lower-case letter resolves here and then fails to match in
              EduCore.
            */}
            <span className={styles.fieldHint}>{t('platform.masterData.codeHint')}</span>
          </label>
        )}

        {(dialog.kind === 'CREATE' || dialog.kind === 'VERSION') && (
          <>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.masterData.colLabel')}</span>
              <input
                className={styles.input}
                value={label}
                required
                onChange={(event) => setLabel(event.target.value)}
              />
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.masterData.descriptionLabel')}</span>
              <textarea
                className={styles.textarea}
                rows={2}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.masterData.payloadLabel')}</span>
              <textarea
                className={styles.codeArea}
                rows={6}
                value={payloadText}
                spellCheck={false}
                aria-invalid={payload === null}
                onChange={(event) => setPayloadText(event.target.value)}
              />
              <span className={styles.fieldHint}>{t('platform.masterData.payloadHint')}</span>
              {payload === null && (
                <span className={styles.fieldError} role="alert">
                  {t('platform.policies.invalidJson')}
                </span>
              )}
            </label>
          </>
        )}

        {dialog.kind === 'CREATE' && (
          <Checkbox
            label={t('platform.masterData.publishImmediately')}
            checked={publishImmediately}
            onChange={(event) => setPublishImmediately(event.target.checked)}
          />
        )}

        {dialog.kind === 'STATUS' && (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.masterData.colStatus')}</span>
            <Select
              value={status}
              onChange={(value) => setStatus(value as MasterDataValueStatus)}
              options={SETTABLE_STATUSES.map((candidate) => ({
                value: candidate,
                label: t(`platform.masterData.status.${candidate}`),
              }))}
            />
            {/*
              DEPRECATED is still usable. It is a signal that new work should move
              to the successor, not a withdrawal — that is what RETIRED is for.
            */}
            <span className={styles.fieldHint}>{t('platform.masterData.statusHint')}</span>
          </label>
        )}

        {dialog.kind === 'RETIRE' && (
          <>
            <div className={styles.warningNotice} role="note">
              <Icon name="warning" size={16} aria-hidden="true" />
              <div>
                <p className={styles.warningTitle}>{t('platform.masterData.retireTitle')}</p>
                <p className={styles.warningBody}>{t('platform.masterData.retireBody')}</p>
              </div>
            </div>

            <AsyncPanel state={preview} onRetry={reloadPreview}>
              {(report) => (
                <div className={styles.auditDetail}>
                  <dl className={styles.impactFacts}>
                    <dt>{t('platform.masterData.canRetire')}</dt>
                    <dd>
                      {report.canRetire
                        ? t('platform.lifecycle.impact.yes')
                        : t('platform.lifecycle.impact.no')}
                    </dd>
                    <dt>{t('platform.masterData.blockingReferences')}</dt>
                    <dd>
                      {report.blockingReferences.length > 0
                        ? report.blockingReferences
                            .map((entry) => `${entry.domain}/${entry.code}`)
                            .join(', ')
                        : '—'}
                    </dd>
                    <dt>{t('platform.masterData.blockingUsage')}</dt>
                    <dd>{report.blockingUsage.length > 0 ? report.blockingUsage.join(', ') : '—'}</dd>
                    <dt>{t('platform.masterData.unverifiableIn')}</dt>
                    <dd>{report.unverifiableIn.length > 0 ? report.unverifiableIn.join(', ') : '—'}</dd>
                  </dl>

                  {/*
                    Two kinds of refusal, and only one of them can be overridden.
                    Records in this database still citing the value is absolute;
                    usage in a service core cannot query is a risk the operator can
                    accept in writing.
                  */}
                  {report.unverifiableIn.length > 0 && (
                    <Checkbox
                      label={t('platform.masterData.acknowledgeUnverifiable', {
                        services: report.unverifiableIn.join(', '),
                      })}
                      checked={acknowledgeUnverifiable}
                      onChange={(event) => setAcknowledgeUnverifiable(event.target.checked)}
                    />
                  )}
                </div>
              )}
            </AsyncPanel>
          </>
        )}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {dialog.kind === 'CREATE'
              ? t('platform.masterData.changeReasonOptional')
              : t('platform.confirm.reasonLabel')}
          </span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={changeReason}
            required={dialog.kind !== 'CREATE'}
            aria-invalid={
              dialog.kind !== 'CREATE' && changeReason.length > 0 && changeReason.trim().length < MIN_REASON
            }
            onChange={(event) => setChangeReason(event.target.value)}
          />
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting
              ? t('platform.confirm.submitting')
              : t(`platform.masterData.dialog.${dialog.kind}.confirm`)}
          </button>
        </div>
      </form>
    </Modal>
  );
}
